// Home: where the firm stands, the scorecard and the messages from the developer.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader } from '@tatra/ui';
import { Loading, Stat } from '../components.js';
import { get, post, type Broadcast, type Scorecard } from '../lib/api.js';
import { dateOnly, rupees } from '../lib/format.js';
import { useSession } from '../lib/session.js';

export function HomePage() {
  const { me, approved } = useSession();
  if (!me) return <Loading />;
  return (
    <>
      <PageHeader
        title={`Hello, ${me.me.name.split(' ')[0]}`}
        description={me.name}
        actions={
          approved && (
            <Link to="/register">
              <Button>Register a lead</Button>
            </Link>
          )
        }
      />
      {!approved && <StatusPanel />}
      {approved && (
        <div className="space-y-6">
          <Scorecards />
          <Messages />
        </div>
      )}
    </>
  );
}

function StatusPanel() {
  const { me } = useSession();
  if (!me) return null;
  const copy = {
    PENDING: {
      tone: 'warning' as const,
      title: 'Your application is with our team',
      text: me.missing.length
        ? 'We cannot approve it until the details below are added.'
        : 'We are reviewing your details. You will be able to register customers as soon as you are approved.',
    },
    REJECTED: {
      tone: 'danger' as const,
      title: 'Your application was not approved',
      text: 'Please correct the details and send them again.',
    },
    SUSPENDED: {
      tone: 'danger' as const,
      title: 'Your account is paused',
      text: 'You cannot register new customers right now. Your relationship manager can tell you more.',
    },
    APPROVED: { tone: 'success' as const, title: '', text: '' },
  }[me.status];
  return (
    <Card className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={copy.tone}>
          {me.status === 'PENDING' ? 'In review' : me.status === 'REJECTED' ? 'Not approved' : 'Paused'}
        </Badge>
        <h2 className="font-medium text-text">{copy.title}</h2>
      </div>
      <p className="text-sm text-text-muted">{copy.text}</p>
      {me.decisionNote && (
        <p className="rounded-md bg-surface-hover p-3 text-sm text-text">
          <span className="font-medium">Note from our team: </span>
          {me.decisionNote}
        </p>
      )}
      {me.status !== 'SUSPENDED' && me.missing.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-text">
          {me.missing.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
      {me.status !== 'SUSPENDED' && me.me.role === 'OWNER' && (
        <Link to="/profile">
          <Button>{me.status === 'REJECTED' ? 'Correct and resubmit' : 'Add your details'}</Button>
        </Link>
      )}
    </Card>
  );
}

function Scorecards() {
  const money = useSession().config?.money !== false;
  const q = useQuery({ queryKey: ['scorecard'], queryFn: () => get<Scorecard>('/scorecard') });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState onRetry={() => void q.refetch()} />;
  const s = q.data;
  return (
    <section aria-labelledby="score-h">
      <h2 id="score-h" className="mb-2 text-sm font-semibold text-text">
        Your numbers
      </h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Leads registered" value={s.leads} />
        <Stat label="Site visits" value={s.visits} hint={`${s.visitedLeads} customers visited`} />
        <Stat label="Bookings" value={s.bookings} hint={`${s.conversion}% of leads`} />
        <Stat label="Not accepted" value={s.refused} />
        {money && (
          <>
            <Stat label="Commission earned" value={rupees(s.commissionEarned)} />
            <Stat label="Commission paid" value={rupees(s.commissionPaid)} />
            <Stat label="Still to receive" value={rupees(s.commissionOutstanding)} />
          </>
        )}
      </div>
    </section>
  );
}

const KIND: Record<string, string> = { LAUNCH: 'New launch', OFFER: 'Offer', UPDATE: 'Update' };

function Messages() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['broadcasts'],
    queryFn: () => get<{ unread: number; items: Broadcast[] }>('/broadcasts?limit=20'),
  });
  const read = useMutation({
    mutationFn: (id: string) => post(`/broadcasts/${id}/read`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['broadcasts'] }),
  });
  return (
    <section aria-labelledby="msg-h">
      <h2 id="msg-h" className="mb-2 flex items-center gap-2 text-sm font-semibold text-text">
        Messages from us
        {!!q.data?.unread && <Badge tone="primary">{q.data.unread} new</Badge>}
      </h2>
      {q.isLoading ? (
        <Loading />
      ) : q.isError || !q.data ? (
        <ErrorState onRetry={() => void q.refetch()} />
      ) : q.data.items.length === 0 ? (
        <EmptyState
          title="No messages yet"
          description="Launches and offers for your projects will show up here."
        />
      ) : (
        <ul className="space-y-3">
          {q.data.items.map((b) => (
            <li key={b.id}>
              <Card className="space-y-2 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={b.readAt ? 'neutral' : 'primary'}>{KIND[b.kind] ?? b.kind}</Badge>
                  <span className="text-xs text-text-faint">{dateOnly(b.sentAt)}</span>
                  {b.projects.length > 0 && (
                    <span className="text-xs text-text-muted">{b.projects.join(', ')}</span>
                  )}
                </div>
                <h3 className="font-medium text-text">{b.title}</h3>
                <p className="whitespace-pre-line text-sm text-text-muted">{b.body}</p>
                {!b.readAt && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={read.isPending && read.variables === b.id}
                    onClick={() => read.mutate(b.id)}
                  >
                    Mark as read
                  </Button>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
